import { Shield, Lock, CreditCard, CheckCircle, Award, RefreshCw } from 'lucide-react';

interface TrustBadgesProps {
  variant?: 'default' | 'compact' | 'horizontal';
  showLabels?: boolean;
}

export default function TrustBadges({ variant = 'default', showLabels = true }: TrustBadgesProps) {
  const badges = [
    {
      icon: Shield,
      label: '30-Tage Geld-zurück',
      description: 'Garantie',
      color: 'green',
    },
    {
      icon: Lock,
      label: 'SSL-verschlüsselt',
      description: 'Sichere Zahlung',
      color: 'blue',
    },
    {
      icon: CreditCard,
      label: 'Digistore24',
      description: 'Sicherer Zahlungsanbieter',
      color: 'purple',
    },
    {
      icon: CheckCircle,
      label: '100% Zufriedenheit',
      description: 'Über 1000 glückliche Kunden',
      color: 'green',
    },
  ];

  if (variant === 'compact') {
    return (
      <div className="flex items-center justify-center gap-4 flex-wrap">
        {badges.slice(0, 3).map((badge, index) => (
          <div
            key={index}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-3 py-2 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300"
          >
            <badge.icon className="w-4 h-4 text-green-600" />
            {showLabels && (
              <span className="text-xs font-medium text-gray-700">{badge.label}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'horizontal') {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {badges.map((badge, index) => {
          const colorClasses = {
            green: 'bg-green-50 border-green-200 text-green-700',
            blue: 'bg-blue-50 border-blue-200 text-blue-700',
            purple: 'bg-purple-50 border-purple-200 text-purple-700',
          }[badge.color];

          return (
            <div
              key={index}
              className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 ${colorClasses} hover:scale-105 transition-transform duration-300 shadow-sm hover:shadow-md`}
            >
              <badge.icon className="w-6 h-6 mb-1" />
              {showLabels && (
                <div className="text-center">
                  <div className="text-xs font-bold">{badge.label}</div>
                  <div className="text-[10px] opacity-75">{badge.description}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 border border-green-200 shadow-lg">
      <div className="flex items-center gap-2 mb-4">
        <Award className="w-5 h-5 text-green-600" />
        <h3 className="font-bold text-gray-900">Deine Sicherheit ist garantiert</h3>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {badges.map((badge, index) => {
          const colorClasses = {
            green: 'from-green-500 to-green-600',
            blue: 'from-blue-500 to-blue-600',
            purple: 'from-purple-500 to-purple-600',
          }[badge.color];

          return (
            <div
              key={index}
              className="bg-white rounded-xl p-4 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
            >
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${colorClasses} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                  <badge.icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-gray-900 mb-1">{badge.label}</div>
                  <div className="text-sm text-gray-600">{badge.description}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-600">
        <RefreshCw className="w-4 h-4 text-green-600" />
        <span>Bei weniger als 5 kg Verlust: Geld zurück – ohne Wenn und Aber</span>
      </div>
    </div>
  );
}
